import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, Download, AlertTriangle, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";

const WINDOWS = [30, 60, 90, 180];

const SortTh = ({ label, k, sort, setSort }) => {
  const active = sort.key === k;
  return (
    <th
      className="text-right px-3 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => setSort({ key: k, dir: active && sort.dir === "desc" ? "asc" : "desc" })}
      data-testid={`rr-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sort.dir === "desc" ? <ArrowDown className="h-3 w-3" aria-hidden="true" /> : <ArrowUp className="h-3 w-3" aria-hidden="true" />)}
      </span>
    </th>
  );
};

const Kpi = ({ label, value, sub, testId, tone }) => (
  <div className="bg-card border border-border rounded-lg p-5 shadow-sm" data-testid={testId}>
    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 ${tone || ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
  </div>
);

export const ReimbursementRecovery = () => {
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [onlyClaimable, setOnlyClaimable] = useState(true);
  const [sort, setSort] = useState({ key: "claimable_value_asp", dir: "desc" });

  const load = (refresh = false) =>
    api.get(`/tools/reimbursement-recovery?days=${days}${refresh ? "&refresh=true" : ""}`).then(({ data }) => setData(data));
  useEffect(() => { setData(null); load(); }, [days]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/tools/reimbursement-recovery/sync?days=${days}`);
      toast.info("Pulling the inventory ledger and reimbursements from Amazon — 3–8 minutes");
      setTimeout(async () => { await load(true); setSyncing(false); toast.success("Recovery data refreshed"); }, 210000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
      setSyncing(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/tools/reimbursement-recovery/export?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reimbursement-recovery-${days}d.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const query = q.trim().toLowerCase();
    let list = data.rows.filter((r) =>
      (!onlyClaimable || r.claimable_units > 0) &&
      (!query || (r.title || "").toLowerCase().includes(query) ||
        (r.sku || "").toLowerCase().includes(query) ||
        (r.asin || "").toLowerCase().includes(query)));
    return [...list].sort((a, b) => {
      const va = a[sort.key] ?? -1, vb = b[sort.key] ?? -1;
      return sort.dir === "desc" ? vb - va : va - vb;
    });
  }, [data, q, onlyClaimable, sort]);

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  const k = data.kpis || {};

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="reimb-recovery">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Reimbursement Recovery</h2>
          <p className="text-xs text-muted-foreground">
            Amazon&apos;s inventory ledger (lost / damaged / disposed) minus what they already reimbursed
            {data.ledger_as_of && ` · ledger as of ${data.ledger_as_of}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="rr-window-toggle">
            {WINDOWS.map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`rr-window-${d}`}
                      className={`px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors ${days === d ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {d}d
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportXlsx} data-testid="rr-export-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Export Excel
          </Button>
          <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing} data-testid="rr-sync-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing ? "Refreshing…" : "Refresh from Amazon"}
          </Button>
        </div>
      </div>

      {data.refreshing && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="rr-refreshing">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">Fetching the reports from Amazon</p>
            <p className="text-xs text-muted-foreground mt-1">{data.error || "Amazon takes a few minutes to build the ledger and reimbursements reports. Reload this tab shortly."}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Claimable (at ASP)" value={inr(k.claimable_value_asp || 0)} sub={`${(k.claimable_units || 0).toLocaleString("en-IN")} units · ${(k.skus_claimable || 0)} SKUs`} testId="rr-kpi-asp" tone="text-emerald-600 dark:text-emerald-400" />
        <Kpi label="Claimable (settlement net)" value={inr(k.claimable_value_settle || 0)} sub="realised net per unit" testId="rr-kpi-settle" />
        <Kpi label="Lost / damaged units" value={(k.loss_units || 0).toLocaleString("en-IN")}
             sub={`${k.skus_with_loss || 0} SKUs in ${days} days${k.unknown_units ? ` · ${k.unknown_units} unknown events (not claimed)` : ""}`}
             testId="rr-kpi-loss" tone="text-red-600 dark:text-red-400" />
        <Kpi label="Already reimbursed" value={inr(k.reimbursed_amount || 0)} sub={`${(k.reimbursed_units || 0).toLocaleString("en-IN")} units credited`} testId="rr-kpi-reimbursed" />
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="rr-note">
        <span>{data.note}</span>
        <a href="https://sellercentral.amazon.in/reportcentral/FBA_INVENTORY_RECONCILIATION" target="_blank" rel="noreferrer"
           className="text-blue-600 hover:underline shrink-0 inline-flex items-center gap-1" data-testid="rr-seller-central-link">
          Open reconciliation <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU or ASIN…"
                 className="pl-9 h-9 rounded-full" data-testid="rr-search-input" />
        </div>
        <button onClick={() => setOnlyClaimable(!onlyClaimable)} data-testid="rr-toggle-claimable"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${onlyClaimable ? "bg-blue-600 text-white border-blue-600" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {onlyClaimable ? "Claimable only" : "All loss rows"}
        </button>
        <span className="text-xs text-muted-foreground" data-testid="rr-count">{rows.length.toLocaleString("en-IN")} SKUs</span>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto w-full max-w-full min-w-0">
        <table className="w-full text-sm min-w-[1150px]" data-testid="rr-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 font-semibold">Product</th>
              <SortTh label="Lost" k="lost" sort={sort} setSort={setSort} />
              <SortTh label="Damaged" k="damaged" sort={sort} setSort={setSort} />
              <SortTh label="Disposed" k="disposed" sort={sort} setSort={setSort} />
              <SortTh label="Found" k="found" sort={sort} setSort={setSort} />
              <SortTh label="Reimbursed" k="reimbursed_units" sort={sort} setSort={setSort} />
              <SortTh label="Claimable units" k="claimable_units" sort={sort} setSort={setSort} />
              <SortTh label="ASP" k="asp" sort={sort} setSort={setSort} />
              <SortTh label="Claimable ₹ (ASP)" k="claimable_value_asp" sort={sort} setSort={setSort} />
              <SortTh label="Claimable ₹ (settled)" k="claimable_value_settle" sort={sort} setSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map((r) => (
              <tr key={r.sku} className={`border-t border-border hover:bg-muted/40 transition-colors ${r.claimable_units > 0 ? "" : "opacity-70"}`} data-testid={`rr-row-${r.sku}`}>
                <td className="py-2 pl-3 w-14"><SkuThumb sku={r.sku} alt={r.title} /></td>
                <td className="px-3 py-2">
                  <p className="font-medium max-w-[20rem] truncate" title={r.title}>{r.title || r.sku}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {r.sku}{r.fcs?.length ? ` · ${r.fcs.join(", ")}` : ""}
                  </p>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.lost.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.damaged.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.disposed.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{r.found.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.reimbursed_units.toLocaleString("en-IN")}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.claimable_units > 0 ? "text-red-600 dark:text-red-400" : ""}`}>
                  {r.claimable_units.toLocaleString("en-IN")}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.asp ? inr(r.asp) : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{inr(r.claimable_value_asp)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.settle_per_unit ? inr(r.claimable_value_settle) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !data.refreshing && (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="rr-empty">
            {q ? "No products match your search." : onlyClaimable ? "Nothing claimable in this window — Amazon reimbursed everything they lost." : "No inventory losses recorded in this window."}
          </p>
        )}
        {rows.length > 300 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">Showing top 300 of {rows.length} — export to Excel for the full list</p>
        )}
      </div>
    </div>
  );
};
